import { RouteLocation } from 'vue-router';
import Auth from './auth'; // To be refactored later
import i18n from '@/i18n';
import { ElMessageBox } from 'element-plus';
import { useThemeStore } from '@/stores/themeStore';
import { useUserStore } from '@/stores/userStore';
import { useFeatureFlagStore } from '@/stores/featureFlagStore';
import * as userService from '@/services/api/userService'; // Keep this for now
import { getUserRoleAndDomainByEmail } from '@/services/api/userService';
import { getFeatureFlags } from '@/services/api/featureFlagService';
import { FeatureFlag } from '@/types/api/FeatureFlag/FeatureFlag';
import { UserType } from '@/types/api/NewUser/User';
import { changeUserId } from '@/utils/auth';
import { endpointAuthSystem } from '@/services/api/api';
import Cookies from 'js-cookie';
import { Key } from '@/types/constants/Key';
import { Role } from '@/types/enum/user';

/**
 * Orchestrates the full session initialization, including authentication,
 * user data fetching, and feature flag loading.
 */
export async function initializeSession(): Promise<void> {
  const userStore = useUserStore();
  const themeStore = useThemeStore();
  const featureFlagStore = useFeatureFlagStore();

  userStore.setIsAuthenticating(true);

  try {
    const user = await tryGetAuthenticatedUser();
    if (!user) {
      await handleUnauthenticatedFlow(userStore, themeStore, featureFlagStore);
      return;
    }

    const normalizedUser = await changeUserId(user);
    userStore.setUser(normalizedUser);
    userStore.setIsAuthenticated(Boolean(Cookies.get(Key.cookie.access_token_key)));

    const { domain, role } = await safeGetRoleAndDomain(normalizedUser.email);
    userStore.setDomain(domain ?? null);
    setRolesFromString(userStore, role);

    const flags = await getFeatureFlags();
    featureFlagStore.setFeatureFlags(toFeatureFlagRecord(flags));

    applyDomainTheme(themeStore, domain);
  } catch (err: unknown) {
    console.error('Unexpected initialization error:', err);
  } finally {
    userStore.setIsAuthenticating(false);
  }
}

async function tryGetAuthenticatedUser(): Promise<UserType | null> {
  try {
    const token = Cookies.get(Key.cookie.access_token_key);
    if (!token) return null;

    const api = endpointAuthSystem();
    const formData = new FormData();
    formData.append(Key.cookie.access_token_key, token);

    const response = await api.post('api/cekoon/login/authentik/check-user', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data?.UserData?.user ?? null;
  } catch (e) {
    console.error('Authentication check failed:', e);
    return null;
  }
}

async function safeGetRoleAndDomain(email: string): Promise<{
  domain: Awaited<ReturnType<typeof getUserRoleAndDomainByEmail>>['domain'] | null;
  role: string | null;
}> {
  try {
    const res = await getUserRoleAndDomainByEmail(email);
    return { domain: res.domain ?? null, role: res.role ?? null };
  } catch (e) {
    console.error('Failed to fetch role/domain by email:', e);
    return { domain: null, role: null };
  }
}

function setRolesFromString(userStore: ReturnType<typeof useUserStore>, role: string | null): void {
  if (!role) {
    userStore.setRoles([]);
    return;
  }
  const normalized = role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  const roleEnum: Role | undefined = (Role as any)[normalized] as Role | undefined;
  userStore.setRoles(roleEnum ? [roleEnum] : []);
}

function toFeatureFlagRecord(flags: Array<{ key: string; value: boolean }>): Record<keyof FeatureFlag, boolean> {
  return flags.reduce((acc, current) => {
    const key = current.key as keyof FeatureFlag;
    acc[key] = current.value;
    return acc;
  }, {} as Record<keyof FeatureFlag, boolean>);
}

function applyDomainTheme(
  themeStore: ReturnType<typeof useThemeStore>,
  domain: { colorSchema?: Iterable<any> | null } | null
): void {
  if (!domain) return;
  themeStore.setThemeFromSupplier(domain.colorSchema ?? []);
}

async function handleUnauthenticatedFlow(
  userStore: ReturnType<typeof useUserStore>,
  themeStore: ReturnType<typeof useThemeStore>,
  featureFlagStore: ReturnType<typeof useFeatureFlagStore>
): Promise<void> {
  userStore.setUser(null);
  userStore.setRoles([]);
  userStore.setIsAuthenticated(false);

  const supplierUrl = new URLSearchParams(window.location.search).get('supplier') || 'aidddo';

  try {
    const publicDomain = await userService.getDomainByUrlSuffix(supplierUrl);
    userStore.setDomain(publicDomain ?? null);
    themeStore.setThemeFromSupplier(publicDomain?.colorSchema ?? []);
  } catch (e) {
    console.error('Failed to fetch public domain:', e);
  }

  const flags = await getFeatureFlags();
  featureFlagStore.setFeatureFlags(toFeatureFlagRecord(flags));

  const auth = Auth.getInstance();
  await auth.handleAuthentication();
}
